import React from 'react';
// import { ReactSVG } from 'react-svg'; // Not needed currently

// UI components from shared T-Mobile component library
import { RawData, TmoCard } from '@tmobile/tmo-bps-syncup-web-component-lib';

// Isolated delete action component (confirmation + delete handling)
import FeatureFlagDeleteAction from './FeatureFlagDeleteAction';

// Utility for conditionally joining class names
import classNames from 'classnames';

// Local styles
import style from './FeatureFlagItem.module.css';
import gridStyle from '../ItemDetailList/ItemDetailList.module.css';

/**
 * Feature flag status icons
 * -------------------------
 * We attempt to load SVG icons at runtime.
 * If icons are missing (e.g. build-time or environment issue),
 * we gracefully fall back to a simple text-based indicator.
 */
let flagEnabledIcon, flagDisabledIcon;

try {
  flagEnabledIcon = require('../../static/assets/icons/feature-flag-enabled.svg')
    .default;
} catch (e) {
  // Icon not available – will use fallback UI
  flagEnabledIcon = null;
}

try {
  flagDisabledIcon = require('../../static/assets/icons/feature-flag-disabled.svg')
    .default;
} catch (e) {
  // Icon not available – will use fallback UI
  flagDisabledIcon = null;
}

/**
 * FeatureFlagItem
 * ----------------
 * Renders a single feature flag card with:
 * - Feature identifier
 * - Current enabled/disabled status
 * - Visual status indicator
 * - Actions (view raw details, delete)
 */
function FeatureFlagItem({ data, onDelete, onUpdate }) {
  // Destructure commonly used properties from feature flag data
  const { featureId, isEnabled } = data;

  /**
   * Resolve the unique identifier safely.
   * Some APIs return `id`, others return `featureId`,
   * so we normalize it here to avoid conditional logic elsewhere.
   */
  const resolvedId = data.id || data.featureId;

  /**
   * (Currently unused)
   * Update handler kept commented intentionally.
   * This allows easy re-enablement of update functionality
   * without restructuring the component later.
   */
  // const handleUpdate = () => {
  //   if (onUpdate) {
  //     onUpdate(resolvedId, data);
  //   }
  // };

  /**
   * Renders the visual status indicator for the feature flag.
   *
   * Priority:
   * 1. Use SVG icons if available (preferred UX)
   * 2. Fall back to a styled circle with ✓ / ✗ if icons are missing
   */
  const renderStatusIcon = () => {
    if (flagEnabledIcon && flagDisabledIcon) {
      return (
        <img
          className={style.icon}
          alt="feature flag status"
          src={isEnabled ? flagEnabledIcon : flagDisabledIcon}
        />
      );
    }

    // Fallback UI when icons are unavailable
    return (
      <div
        className={`${style.status_circle} ${
          isEnabled ? style.enabled_circle : style.disabled_circle
        }`}
        title={isEnabled ? 'Feature Flag Enabled' : 'Feature Flag Disabled'}
      >
        {isEnabled ? '✓' : '✗'}
      </div>
    );
  };

  return (
    <div className={style.container}>
      <TmoCard>
        {/* Card content area */}
        <div className={style.card_container}>
          {/* Feature Flag Identifier */}
          <div className={classNames(gridStyle.row, style.app_id_row)}>
            <label className={gridStyle.label}>Feature ID</label>
            <div className={gridStyle.value}>{featureId}</div>
          </div>

          {/* Visual status icon positioned separately for layout flexibility */}
          <div className={style.platform_icon}>{renderStatusIcon()}</div>

          {/* Human-readable status */}
          <div className={classNames(gridStyle.row, style.feature_id_row)}>
            <label className={gridStyle.label}>Status</label>
            <div className={gridStyle.value}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </div>
          </div>
        </div>

        {/* Action Section */}
        <div className={style.action}>
          {/* 
            RawData:
            - Opens a slide-in modal
            - Displays the complete feature flag object
            - Useful for debugging, audit, and support scenarios
          */}
          <RawData
            rawObject={data}
            title="Feature Flag Details"
            type="Flag Details"
            usePortal={true}
            modalType="slide"
            slideFrom="right"
            buttonType="magenta_link"
            buttonText="View Details"
            showCloseButton={true}
            overlayClose={true}
            className={style.raw_data_button}
          />

          {/* 
            Delete Action:
            - Handles confirmation modal and delete callback
            - Keeps this component focused on display concerns
          */}
          <FeatureFlagDeleteAction
            featureId={resolvedId}
            onDelete={onDelete}
            className={style.button}
          />
        </div>
      </TmoCard>
    </div>
  );
}

export default FeatureFlagItem;
